import React, { useState, useEffect, useMemo } from 'react';
import { filter, first, find, isEmpty } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { Plus } from 'lucide-react';
import {
  TAutomationActionOption,
  useGetMessageTemplateById,
  MessageTemplate,
  MessageTemplateInput,
} from '@frontend/app/hooks';
import { TemplateTransportType, TemplateBodyType, TemplateType } from '@frontend/app/types/globalTypes';
import { MessageTemplateEditor } from '@frontend/app/containers/Projects/AutomationConfig/ConfigDrawer/MessageTemplateEditor';
import { useAuth } from '@frontend/context/authContext';
import { Loader2 } from 'lucide-react';
import { AutomationNodeV2 } from '../../types';
import { Button } from '@/shadcn/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shadcn/components/ui/select';
import { Label } from '@/shadcn/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shadcn/components/ui/tooltip';

interface SendEmailActionFormProps {
  selectedNode: AutomationNodeV2;
  onUpdateNode: (nodeId: string, updates: Partial<AutomationNodeV2>) => void;
  actionOptions: TAutomationActionOption[];
  onUpdateMessageTemplate: (updatedTemplate: MessageTemplate | MessageTemplateInput) => void;
  resetMessageTemplate: boolean;
  onSave: () => void;
  onCancel: () => void;
}

export const SendEmailActionForm: React.FC<SendEmailActionFormProps> = ({
  selectedNode,
  onUpdateNode,
  actionOptions,
  onUpdateMessageTemplate,
  resetMessageTemplate,
  onSave,
  onCancel,
}) => {
  const { user } = useAuth();
  const [templateId, setTemplateId] = useState<number>();
  const [resourceId, setResourceId] = useState<number>();
  const [fromPreviousSender, setFromPreviousSender] = useState<boolean>();
  const [templateValidationError, setTemplateValidationError] = useState<string>('');
  const [resourceValidationError, setResourceValidationError] = useState<string>('');
  const [newTemplate, setNewTemplate] = useState<MessageTemplateInput>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | MessageTemplateInput>(null);
  const [createNewTemplateDisabled, setCreateNewTemplateDisabled] = useState<boolean>(false);

  const resources = useMemo(() => (
    actionOptions && actionOptions[0]?.emailResources ? actionOptions[0].emailResources.filter(
      (resource) => (
        resource.userId === user.sub
        || resource.id === resourceId
        || resource.displayName === 'Previous Sender'
      ),
    ) : []
  ), [actionOptions, user.sub, resourceId]);

  const selectedResourceExist = useMemo(() => !isEmpty(find(resources, { id: resourceId })), [resources, resourceId]);

  // Initialize state from selectedNode metadata
  useEffect(() => {
    if (selectedNode && selectedNode.type === 'ACTION') {
      setTemplateId(selectedNode.metadata.templateId);
      setResourceId(selectedNode.metadata.resourceId);
      setFromPreviousSender(selectedNode.metadata.fromPreviousSender);
    }
  }, [selectedNode]);

  useEffect(() => {
    if (resetMessageTemplate) {
      setCreateNewTemplateDisabled(false);
      setNewTemplate(null);
    }
  }, [resetMessageTemplate]);

  const actionNodeDefaultTemplateData = useMemo(() => {
    if (!actionOptions || !actionOptions[0]?.messageTemplates) return null;
    const filtered = filter(
      actionOptions[0].messageTemplates,
      (template) => template.messageTemplateType !== TemplateType.GENERIC,
    );
    return first(filtered);
  }, [actionOptions]);

  const {
    data: {
      template: defaultAutomationTemplate = null,
    } = {},
  } = useGetMessageTemplateById({
    variables: {
      templateId: actionNodeDefaultTemplateData?.id,
    },
    skip: !actionNodeDefaultTemplateData?.id,
  });

  const {
    data: {
      template = null,
    } = {},
    loading: isTemplateLoading,
  } = useGetMessageTemplateById({
    variables: {
      templateId,
    },
    skip: !templateId,
  });

  useEffect(() => {
    setSelectedTemplate(template || newTemplate);
  }, [template, newTemplate]);

  const handleCreateNewTemplate = () => {
    setCreateNewTemplateDisabled(true);
    setTemplateId(null);
    setNewTemplate({
      id: uuidv4(),
      title: 'New Template',
      subject: 'Enter the subject here',
      text: defaultAutomationTemplate?.text,
      transportType: TemplateTransportType.EMAIL,
      bodyType: TemplateBodyType.HTML,
      type: TemplateType.GENERIC,
    });
  };

  const handleTemplateChange = (templateId) => {
    setCreateNewTemplateDisabled(false);
    setTemplateId(templateId);
  };

  const validateAction = () => {
    const noTemplateError = 'Please select an email template';
    const noResourceError = 'Please select an email';
    const noError = '';

    const templateError = (!templateId && !newTemplate) ? noTemplateError : noError;
    const resourceError = (!resourceId && !fromPreviousSender) || !selectedResourceExist ? noResourceError : noError;

    setTemplateValidationError(templateError);
    setResourceValidationError(resourceError);

    return templateError === noError && resourceError === noError;
  };

  const handleSave = () => {
    if (!validateAction()) return;

    // Update the node metadata with the form values
    onUpdateNode(selectedNode.id, {
      metadata: {
        ...selectedNode.metadata,
        templateId,
        resourceId,
        fromPreviousSender,
      },
    });

    onSave();
  };

  const onEmailResourceUpdate = (resourceId, resource) => {
    setResourceId(resourceId);
    setFromPreviousSender(resource.data.fromPreviousSender);
  };

  const handleCancel = () => {
    setCreateNewTemplateDisabled(false);
    setNewTemplate(null);
    onCancel();
  };

  return (
    <div className="h-full flex flex-col">
      {/* Scrollable content area with bottom padding for buttons */}
      <div className="flex-1 overflow-y-auto space-y-4 px-1 pb-20">
        {/* Send email from section */}
        <div className="space-y-2">
          <Label htmlFor="email-resource">Send email from</Label>
          <Select
            value={selectedResourceExist ? String(resourceId) : undefined}
            onValueChange={(value) => {
              const resource = resources.find((r) => String(r.id) === value);
              if (resource) {
                onEmailResourceUpdate(resource.id, { data: resource });
              }
            }}
          >
            <SelectTrigger id="email-resource">
              <SelectValue placeholder="Select email resource" />
            </SelectTrigger>
            <SelectContent>
              {resources.map((resource) => (
                <SelectItem key={resource.id} value={String(resource.id)}>
                  {resource.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {resourceValidationError && (
            <p className="text-sm text-red-500">{resourceValidationError}</p>
          )}
        </div>

        {/* Email Template section */}
        <div className="space-y-2">
          <Label htmlFor="email-template">Email Template</Label>
          <div className="flex gap-2">
            <Select
              value={templateId ? String(templateId) : (newTemplate?.title || undefined)}
              onValueChange={(value) => {
                const numValue = parseInt(value, 10);
                if (!isNaN(numValue)) {
                  handleTemplateChange(numValue);
                } else {
                  // Handle new template case
                  handleTemplateChange(null);
                }
              }}
            >
              <SelectTrigger id="email-template" className="flex-1">
                <SelectValue placeholder="Select email template" />
              </SelectTrigger>
              <SelectContent>
                {(actionOptions[0]?.messageTemplates || []).map((template) => (
                  <SelectItem key={template.id} value={String(template.id)}>
                    {template.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCreateNewTemplate}
                    disabled={createNewTemplateDisabled}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Create new template</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {templateValidationError && (
            <p className="text-sm text-red-500">{templateValidationError}</p>
          )}
        </div>

        {/* Template Editor */}
        {!selectedTemplate && isTemplateLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}
        {selectedTemplate && (
          <MessageTemplateEditor
            messageTemplate={selectedTemplate}
            onUpdateMessageTemplate={onUpdateMessageTemplate}
            resetMessageTemplate={resetMessageTemplate}
          />
        )}
      </div>

      {/* Action buttons - Fixed at bottom */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-start gap-2 p-4 border-t bg-white shadow-lg">
        <Button
          variant="outline"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
        >
          Done
        </Button>
      </div>
    </div>
  );
};
